import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { usePaymentInputs } from 'react-payment-inputs';
import images from 'react-payment-inputs/images';
import { t } from 'i18next';

import ValidatedTextField from '../ValidatedTextField';

import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import PaymentIcon from '@mui/icons-material/Payment';

const PaymentInfo = ({
	formData,
	errors,
	handleInputChange,
	handleInputBlur,
	forwardedRef
}) => {
	const {
		getCardNumberProps,
		getExpiryDateProps,
		getCardImageProps,
		getCVCProps
	} = usePaymentInputs();

	formData.typeOfCard = getCardImageProps({ images })['aria-label'];

	const cardNumberProps = getCardNumberProps({
		onChange: (e) => handleInputChange('cardNumber', e.target.value),
		onBlur: (e) => handleInputBlur('cardNumber', e.target.value)
	});

	const expiryDateProps = getExpiryDateProps({
		onChange: (e) => handleInputChange('expiryDate', e.target.value),
		onBlur: (e) => handleInputBlur('expiryDate', e.target.value)
	});

	const cvvProps = getCVCProps({
		onChange: (e) => handleInputChange('cvc', e.target.value),
		onBlur: (e) => handleInputBlur('cvc', e.target.value)
	});

	React.useImperativeHandle(forwardedRef, () => ({
		clearFields: () => {
			const cardFields = ['cardNumber', 'expiryDate', 'cvc'];
			cardFields.forEach((name) => {
				const input = document.querySelector(`input[name="${name}"]`);
				if (input) input.value = '';
			});
		}
	}));

	return (
		<Paper
			sx={{
				padding: '15px',
				backgroundColor: (theme) => theme.palette.common.white
			}}
		>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				justifyContent='space-between'
				alignItems={{ xs: 'flex-start', sm: 'center' }}
				mb={2}
			>
				<Stack direction='row' alignItems='center' spacing={1}>
					<PaymentIcon />
					<Typography
						variant='h6'
						component='h2'
						sx={{ textTransform: 'uppercase' }}
					>
						{t('paymentInfo:headerText')}
					</Typography>
				</Stack>
				<Stack width={{ xs: '100%', sm: 'auto' }}>
					<Typography
						color={(theme) => theme.palette.error.main}
						fontSize='0.8em'
						textAlign='right'
						alignSelf={{ xs: 'stretch', sm: 'center' }}
					>
						{t('home:allFieldsRequired')}
					</Typography>
				</Stack>
			</Stack>

			<Stack spacing={3}>
				<ValidatedTextField
					name='nameOnCard'
					label={`${t('paymentInfo:nameOnCard')}`}
					formData={formData}
					handleInputChange={handleInputChange}
					handleInputBlur={handleInputBlur}
					errors={errors}
					required
				/>
				<Stack direction='row' gap={2} flexWrap={{ xs: 'wrap', sm: 'nowrap' }}>
					<TextField
						name='cardNumber'
						size='small'
						label={`${t('paymentInfo:cardNumber')}`}
						inputProps={cardNumberProps}
						fullWidth
						error={!!errors.cardNumber}
						helperText={errors.cardNumber || ''}
						required
						InputProps={{
							endAdornment: (
								<InputAdornment position='end'>
									<svg {...getCardImageProps({ images })} />
								</InputAdornment>
							)
						}}
						sx={{
							flex: { xs: '1 1 100%', sm: 2 },
							minWidth: { xs: '100%', sm: 0 }
						}}
					/>

					<TextField
						name='expiryDate'
						size='small'
						label={`${t('paymentInfo:expiryDate')}`}
						inputProps={expiryDateProps}
						error={!!errors.expiryDate}
						helperText={errors.expiryDate || ''}
						required
						fullWidth
						sx={{
							flex: 1,
							minWidth: { xs: '100%', sm: 0 }
						}}
					/>
					<TextField
						name='cvc'
						size='small'
						label={`${t('paymentInfo:cvv')}`}
						inputProps={cvvProps}
						error={!!errors.cvc}
						helperText={errors.cvc || ''}
						required
						fullWidth
						sx={{
							flex: 1,
							minWidth: { xs: '100%', sm: 0 }
						}}
					/>
				</Stack>
			</Stack>
		</Paper>
	);
};
PaymentInfo.propTypes = {
	t: PropTypes.func,
	formData: PropTypes.object,
	errors: PropTypes.object,
	handleInputChange: PropTypes.func,
	handleInputBlur: PropTypes.func,
	forwardedRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({ current: PropTypes.any })
	])
};

const TranslatedPaymentInfo = withTranslation('Home')(PaymentInfo);

export default React.forwardRef((props, ref) => (
	<TranslatedPaymentInfo {...props} forwardedRef={ref} />
));
